// features.ts — reversible feature flags for whole features.

// In-app journaling / reflection writing is intentionally OFF. ReNurture mirrors
// how real therapy works: reflection happens pen to paper, in the user's own
// private notebook — which we (and the evidence) find both more protective and
// more effective than typing into an app. With this off, every reflection points
// the user to their own journal, and the in-app writing surfaces are hidden:
// the onboarding choice, the reflection text box, the "Writing" timeline in Your
// Stuff, and the Settings toggle. Flip to `true` to restore the in-app option
// everywhere (no data is lost while it's off — existing entries are just hidden).
export const IN_APP_JOURNALING = false;

// First-session onboarding (Task 1B): the warm, founder-led, no-account-up-front
// flow (welcome → 5-min relaxation → light idea → quote → route → habit-anchor →
// close). ON by default. The OLD onboarding (sign-in → journal → route) is retained
// and still used when this is `false` — so the change is fully reversible.
export const FIRST_SESSION_ONBOARDING = true;

// Onboarding v2 (web-led re-sequence): the gentler 6-screen new-user path that
// merges the four reassurance screens into one, moves the safety exit to screen 1,
// creates the account up front, and runs the user-type fork + consent BEFORE any
// data is collected — branching to a type-aware entry screen. One controller
// (OnboardingV2) replaces BOTH FirstSession and the post-session ConsentGate when on.
// It reuses the same state flags, so it's fully reversible: flip to `false` to fall
// back to FirstSession + ConsentGate. The trial/buy framing on the welcome + details
// screens is web-only (SHOW_PURCHASE); native stays purchase-free. LIVE — flip to
// `false` to fall back to FirstSession + ConsentGate instantly.
export const FIRST_SESSION_ONBOARDING_V2 = true;

// "Ask ReNurture" — the content-signposting guide (the librarian). She asks in her
// own words; an on-device crisis floor runs first; a server understands the need and
// points her at ReNurture's REAL content, gated by her actual progress.
//
// SHIPS IN PRODUCTION, but the pill is gated at render on the app's Developer mode
// (`progress.state.devMode`) — see App.tsx. Developer mode is PIN-locked (Settings →
// Developer options), so for every normal live user the feature NEVER appears and has
// ZERO effect (no pill, askUnlockedIds returns []). Only someone who unlocks dev mode
// sees it — that's how it can be tested on the live app without touching anyone else's
// experience. Set this to `false` to remove the feature entirely (kill switch).
//
// Still wired to a LOCAL brain server (ASK_BRAIN_URL). To test on the deployed app,
// run the brain locally (rag-prototype `npm run brain:serve`) and enable dev mode —
// the browser reaches your localhost brain (CORS is '*'). Not for real end-user use
// until the classifier moves server-side with consent.
export const ASK_RENURTURE = true;

// Full FOGS self-check offer in the app: the Progress "What we're working on together"
// card (theme chips + "Waiting to assess your needs" → /assessment-fogs). HIDDEN for
// launch (2026-10-10, felt like bloat). The onboarding needs check is unaffected; synced
// fogs results are still stored. Flip to `true` to bring the card back.
export const FOGS_FULL_CHECK = false;

// The Ask endpoint lives on the app's OWN backend (the Replit Express server) at
// POST /ask — same base as BACKEND_URL. The Anthropic key stays server-side there
// (a Replit secret); the client never sees it. Override with VITE_ASK_BRAIN_URL for
// a local server, else falls back to VITE_BACKEND_URL, else the live Replit host.
export const ASK_BRAIN_URL =
  (typeof import.meta !== 'undefined' &&
    ((import.meta as any).env?.VITE_ASK_BRAIN_URL || (import.meta as any).env?.VITE_BACKEND_URL)) ||
  'https://renurtureprogrammeapp.replit.app';

// Programme CTA on Today (activation fix): a prominent, always-visible "Continue /
// Start your programme" card at the top of the arrival screen, so a cold or returning
// user reaches their current lesson in ONE tap — instead of having to answer the
// arrival question, then find the "Rebuild" tile, then reveal the feed. Cold users
// were signing up and never starting the programme (only ~4/15 completed a chapter);
// the course was buried behind the calm-first landing. Fully reversible: flip to
// `false` to remove the card and restore the pure arrival-question landing.
// RETIRED (2026-07-17): the big card duplicated the (renamed) "Continue your
// programme" menu tile and cluttered the calm-first landing. Activation is now
// carried by the onboarding → Video 1 landing + the play-marked menu tile. Flip
// to `true` to bring the card back.
export const SHOW_PROGRAMME_CTA = false;

// Land straight in the first lesson the moment onboarding finishes. For a brand-new
// user that's Video 1 ("You're in the right place") — the programme's own warm
// welcome, which doubles as orientation, so no separate "explainer" video is needed.
// Opens the current lesson (Video 1) the moment onboarding finishes — the strongest
// cure for "signed up but never started". That first lesson stands ALONE: the feed is
// NOT revealed beneath it, so closing or completing it lands on the arrival triage as
// if the app had just been opened, where the first check-in waits under "Waiting on
// you". Reversible: flip to `false` to go straight to the arrival screen instead.
export const LAND_IN_LESSON_ON_ENTRY = true;

// Focused Today (arrival redesign). Committed to the arrival triage: the state-matched
// box, the three universal actions (Continue / Calm / Toolkit), "Waiting on you", the
// rebuild home, and the depth/imagery pass. LIVE by default (cutover 2026-07-20).
// Roll back instantly WITHOUT a code change by setting VITE_FOCUSED_ARRIVAL=false in the
// Cloudflare Pages env (same pattern as VITE_NEW_UI=false).
export const FOCUSED_ARRIVAL =
  !(typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_FOCUSED_ARRIVAL === 'false');

// ── Onboarding rebuild (2026-09, "renurture onboarding journey" deck) ──────────
// The rebuilt pre-sign-in run (splash + five value screens with soft-arrival
// artwork) plus the trim to the screens after it: 'Create my free account',
// spaced-out trial copy, no wordmark on the consent steps, and the research
// agreement points folded into an expandable box.
//
// Started DELIBERATELY iOS-ONLY (staged rollout: `=== 'ios'` → `!== 'web'` →
// `true`), while the live web build (app.renurture.org) stayed exactly as it was
// until this had been tested on device. As of 2026-09-22 it has reached the goal:
// the rebuilt onboarding run ships on iOS, Android, AND web, retiring the old
// SoftEntry everywhere.
//
// The ONE exception is the "reminders" opt-in step: it stays native-only, gated by
// its own independent check in OnboardingV2.tsx's `endStage()` (local notifications
// can't be scheduled from a browser). This flag does not control that distinction.
export const ONBOARDING_2026: boolean = true;

// ── "Today has one job" (2026-09 UI review) ────────────────────────────────────
// One rule settles the whole Today surface: route the user to ONE next thing.
// Behind this flag:
//   • The arrival state PRE-ANSWERS the Calm question — the matched box's
//     "Settle the body" starts the default 2-minute settle directly, instead of
//     opening the 7-emotion chooser (a second triage stacked on the first).
//   • The Calm chooser (from the "Calm your body" action row) opens as a FULL
//     OVERLAY, not an inline insert below the waiting list.
//   • The matched box never duplicates a programme CTA the action rows already
//     carry (one "continue the programme" per screen).
//   • "Waiting on you" becomes "When you're ready" — one card at a time, rotated
//     daily; every card can sit undone forever with no escalation.
//   • Post-lesson, only the flow's own beats remain; the act survey and chapter
//     feedback move to the next arrival as "When you're ready" cards.
//   • The header "Your recovery" (chart) icon hides until there is a story to
//     tell (any lesson, calm session, tool use or check-in).
//   • Locked toolkit tools collapse behind their section headers.
//
// ON for every platform (iOS, Android, web) so the web app matches native.
export const TODAY_ONE_JOB: boolean = true;

// TRIAGE_V2 — redesigned arrival "triage" Today (src/flows/triage/). ON for every platform.
export const TRIAGE_V2: boolean = true;

// TRIAL_FULL_LOCK — once the 10-day trial lapses (auth.featuresLocked: a lapsed non-member),
// EVERYTHING except the Feeling unsafe crisis support, the Self-compassion course, settings /
// account and the research check-in locks behind the membership paywall (greyed with a lock on
// Today, tabs tap through to the unlock wall). Also adds the trial-ended card/sheet and the
// last-3-days countdown card. Flip to false to restore the previous partial locks (meditations,
// most tools and structured Calm only).
export const TRIAL_FULL_LOCK = true;
